import axiosInstance from '../config/apiconfig';

const BASE = '/citations';
function apiError(status, code, message) {
  const e = new Error(message);
  e.status = status;
  e.code = code;
  return e;
}

async function toError(err) {
  if (!err.response) {
    const slow = err.code === 'ECONNABORTED';
    return apiError(0, slow ? 'TIMEOUT' : 'NETWORK', slow ? 'This is taking too long. Please try again.' : 'Cannot reach the server. Check your connection.');
  }
  const { status } = err.response;
  let { data } = err.response;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try { data = JSON.parse(await data.text()); } catch (_) { data = {}; }
  }
  if (status === 401) return apiError(status, 'UNAUTHENTICATED', 'Your session has expired. Please log in again.');
  const e = (data && data.error) || {};
  return apiError(status, e.code || `HTTP_${status}`, e.message || 'Request failed');
}

async function call(method, url, userId, { params, data, responseType, timeout } = {}) {
  if (!userId) throw apiError(401, 'UNAUTHENTICATED', 'Your session has expired. Please log in again.');
  try {
    const res = await axiosInstance.request({
      method,
      url: BASE + url,
      params: method === 'get' ? { ...(params || {}), user_id: userId } : params,
      data: method === 'get' ? undefined : { ...(data || {}), user_id: userId },
      responseType,
      timeout: timeout || 45000,
    });
    return responseType === 'blob' ? res : res.data;
  } catch (err) {
    throw await toError(err);
  }
}

export const citationsApi = {
  usage: (userId) => call('get', '/usage', userId),
  history: (userId, limit = 20) => call('get', '/history', userId, { params: { limit } }),
  search: (userId, { q, court, from, to, page }) => call('get', '/search', userId, {
    params: { q, court: court || 'all', from: from || undefined, to: to || undefined, page: page || 0 },
  }),
  verify: (userId, text) => call('post', '/verify', userId, { data: { text } }),
  doc: (userId, tid) => call('get', `/docs/${encodeURIComponent(tid)}`, userId),
  fragments: (userId, tid, q) => call('get', `/docs/${encodeURIComponent(tid)}/fragments`, userId, { params: { q } }),
  meta: (userId, tid) => call('get', `/docs/${encodeURIComponent(tid)}/meta`, userId),
  /** Full Cited by / Cites list, 10 per page (type: 'citedby' | 'cites'). */
  citeList: (userId, tid, type, page = 0) => call('get', `/docs/${encodeURIComponent(tid)}/list`, userId, { params: { type, page } }),

  /** Downloads the court copy and returns { blob, name }. */
  courtCopy: async (userId, tid) => {
    const res = await call('get', `/docs/${encodeURIComponent(tid)}/court-copy`, userId, { responseType: 'blob', timeout: 90000 });
    // Never save an error reply as a .pdf (some axios interceptors hand error responses back as normal ones).
    const type = (res.data && res.data.type) || '';
    if ((res.status && res.status >= 400) || /json/i.test(type)) {
      let e = {};
      try { e = (JSON.parse(await res.data.text()) || {}).error || {}; } catch (_) { /* not json */ }
      throw apiError(res.status || 502, e.code || 'NO_COURT_COPY', e.message || 'Court copy could not be downloaded.');
    }
    const disposition = res.headers['content-disposition'] || '';
    // If the server's file name can't be read, name the file by what it really is.
    const ext = { 'application/pdf': 'pdf', 'text/html': 'html', 'text/plain': 'txt', 'image/png': 'png', 'image/jpeg': 'jpg', 'image/tiff': 'tif', 'application/msword': 'doc' }[type.split(';')[0].trim()] || 'pdf';
    const name = (disposition.match(/filename="([^"]+)"/) || [])[1] || `judgment-${tid}-court-copy.${ext}`;
    return { blob: res.data, name };
  },
};

/** Save a Blob as a file in the browser. */
export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}